import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Plus, Calendar, Activity, Thermometer } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalConditions = ({ baby }) => {
  const navigate = useNavigate();
  // Always read-only for baby/parent users - only doctors can edit
  const viewOnlyMode = true;
  const [conditions, setConditions] = useState([]);
  const [temperatureReadings, setTemperatureReadings] = useState([]);
  const [bloodPressureReadings, setBloodPressureReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [showTempDialog, setShowTempDialog] = useState(false);
  const [showEditTempDialog, setShowEditTempDialog] = useState(false);
  const [editingTemperature, setEditingTemperature] = useState(null);
  const [showBpDialog, setShowBpDialog] = useState(false);
  const [showEditBpDialog, setShowEditBpDialog] = useState(false);
  const [editingBloodPressure, setEditingBloodPressure] = useState(null);
  const [activeTab, setActiveTab] = useState('temperature');
  const [activeBpTab, setActiveBpTab] = useState('blood_pressure');
  const [formData, setFormData] = useState({
    condition_name: '',
    description: '',
    diagnosed_date: '',
    status: '',
    medication: ''
  });
  const [tempFormData, setTempFormData] = useState({
    temperature: '',
    unit: 'F',
    method: 'Oral',
    date_recorded: '',
    time_recorded: new Date().toTimeString().slice(0, 5),
    notes: ''
  });
  const [bpFormData, setBpFormData] = useState({
    systolic: '',
    diastolic: '',
    pulse: '',
    method: 'Manual',
    date_recorded: '',
    time_recorded: new Date().toTimeString().slice(0, 5),
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Handle date input with auto-advance for temperature readings
  const handleTempDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setTempFormData(prev => ({ ...prev, date_recorded: value }));
  };

  // Handle backspace for natural deletion (temperature dates)
  const handleTempDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setTempFormData(prev => ({ ...prev, date_recorded: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  // Handle date input with auto-advance for medical conditions
  const handleConditionDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, diagnosed_date: value }));
  };

  // Handle backspace for natural deletion (condition dates)
  const handleConditionDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, diagnosed_date: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  // Handle date input with auto-advance for blood pressure readings
  const handleBpDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to DD/MM/YYYY format
    if (value.length <= 10) {
      setBpFormData(prev => ({ ...prev, date_recorded: value }));
    }
  };

  // Handle backspace for natural deletion (blood pressure dates)
  const handleBpDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setBpFormData(prev => ({ ...prev, date_recorded: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [baby]);

  const fetchAllData = async () => {
    try {
      const [conditionsRes, tempRes, bpRes] = await Promise.all([
        axios.get(`${API}/medical-conditions/${baby.id}`),
        axios.get(`${API}/temperature/${baby.id}`),
        axios.get(`${API}/blood-pressure/${baby.id}`)
      ]);
      
      setConditions(conditionsRes.data);
      
      // Sort temperature readings by created_at timestamp (if available), then by date/time
      const sortedReadings = tempRes.data.sort((a, b) => {
        // If created_at exists, use it for more accurate sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        // Otherwise fall back to date_recorded + time_recorded
        const dateA = new Date(`${a.date_recorded} ${a.time_recorded}`);
        const dateB = new Date(`${b.date_recorded} ${b.time_recorded}`);
        return dateB - dateA;
      });
      
      // Sort blood pressure readings by created_at timestamp (if available), then by date/time
      const sortedBpReadings = bpRes.data.sort((a, b) => {
        // If created_at exists, use it for more accurate sorting
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        // Otherwise fall back to date_recorded + time_recorded
        const dateA = new Date(`${a.date_recorded} ${a.time_recorded}`);
        const dateB = new Date(`${b.date_recorded} ${b.time_recorded}`);
        return dateB - dateA;
      });
      
      setTemperatureReadings(sortedReadings);
      setBloodPressureReadings(sortedBpReadings);
      console.log('Temperature readings sorted:', sortedReadings.slice(0, 3).map(r => ({
        temp: r.temperature,
        date: r.date_recorded,
        time: r.time_recorded,
        created_at: r.created_at
      })));
      console.log('Blood pressure readings sorted:', sortedBpReadings.slice(0, 3).map(r => ({
        bp: `${r.systolic}/${r.diastolic}`,
        date: r.date_recorded,
        time: r.time_recorded,
        created_at: r.created_at
      })));
    } catch (error) {
      console.error('Error fetching medical data:', error);
      toast.error('Failed to load medical data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCondition = async (e) => {
    e.preventDefault();
    
    if (!formData.condition_name) {
      toast.error('Please fill in condition name');
      return;
    }

    setSubmitting(true);
    try {
      const conditionData = {
        baby_id: baby.id,
        condition_name: formData.condition_name,
        description: formData.description,
        diagnosed_date: formData.diagnosed_date,
        status: formData.status,
        medication: formData.medication
      };
      
      await axios.post(`${API}/medical-conditions`, conditionData);
      toast.success('Medical condition added successfully!');
      setShowAddDialog(false);
      setFormData({
        condition_name: '',
        description: '',
        diagnosed_date: '',
        status: '',
        medication: ''
      });
      fetchAllData();
    } catch (error) {
      console.error('Error adding medical condition:', error);
      toast.error('Failed to add medical condition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTemperature = async (e) => {
    e.preventDefault();
    
    if (!tempFormData.temperature || !tempFormData.date_recorded || !tempFormData.time_recorded) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const tempData = {
        baby_id: baby.id,
        temperature: parseFloat(tempFormData.temperature),
        unit: tempFormData.unit,
        method: tempFormData.method,
        date_recorded: tempFormData.date_recorded,
        time_recorded: tempFormData.time_recorded,
        notes: tempFormData.notes || null
      };
      
      await axios.post(`${API}/temperature`, tempData);
      toast.success('Temperature reading added successfully!');
      setShowTempDialog(false);
      setActiveTab('temperature'); // Switch to temperature tab to show latest reading
      setTempFormData({
        temperature: '',
        unit: 'F',
        method: 'Oral',
        date_recorded: new Date().toISOString().split('T')[0],
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await fetchAllData();
    } catch (error) {
      console.error('Error adding temperature reading:', error);
      toast.error('Failed to add temperature reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTemperature = async (e) => {
    e.preventDefault();
    
    if (!tempFormData.temperature || !tempFormData.date_recorded || !tempFormData.time_recorded) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const tempData = {
        temperature: parseFloat(tempFormData.temperature),
        unit: tempFormData.unit,
        method: tempFormData.method,
        date_recorded: tempFormData.date_recorded,
        time_recorded: tempFormData.time_recorded,
        notes: tempFormData.notes || null
      };
      
      await axios.put(`${API}/temperature/${editingTemperature.id}`, tempData);
      toast.success('Temperature reading updated successfully!');
      setShowEditTempDialog(false);
      setEditingTemperature(null);
      setActiveTab('temperature'); // Switch to temperature tab to show updated reading
      setTempFormData({
        temperature: '',
        unit: 'F',
        method: 'Oral',
        date_recorded: new Date().toISOString().split('T')[0],
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await fetchAllData();
    } catch (error) {
      console.error('Error updating temperature reading:', error);
      toast.error('Failed to update temperature reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemperature = async (readingId) => {
    console.log('Delete clicked for temperature ID:', readingId);
    
    if (!window.confirm('Are you sure you want to delete this temperature reading?')) {
      console.log('Delete cancelled by user');
      return;
    }

    try {
      console.log('Sending DELETE request to:', `${API}/temperature/${readingId}`);
      const response = await axios.delete(`${API}/temperature/${readingId}`);
      console.log('Delete response:', response.data);
      
      toast.success('Temperature reading deleted successfully!');
      await fetchAllData();
      console.log('Data refreshed after deletion');
      // Stay on history tab after deletion
    } catch (error) {
      console.error('Error deleting temperature reading:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to delete temperature reading');
    }
  };

  const startEditTemperature = (reading) => {
    setEditingTemperature(reading);
    setTempFormData({
      temperature: reading.temperature.toString(),
      unit: reading.unit,
      method: reading.method,
      date_recorded: reading.date_recorded,
      time_recorded: reading.time_recorded,
      notes: reading.notes || ''
    });
    setShowEditTempDialog(true);
  };

  // Blood Pressure handlers
  const handleAddBloodPressure = async (e) => {
    e.preventDefault();
    
    if (!bpFormData.systolic || !bpFormData.diastolic || !bpFormData.date_recorded || !bpFormData.time_recorded) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const bpData = {
        baby_id: baby.id,
        systolic: parseInt(bpFormData.systolic),
        diastolic: parseInt(bpFormData.diastolic),
        pulse: bpFormData.pulse ? parseInt(bpFormData.pulse) : null,
        method: bpFormData.method,
        date_recorded: bpFormData.date_recorded,
        time_recorded: bpFormData.time_recorded,
        notes: bpFormData.notes || null
      };
      
      await axios.post(`${API}/blood-pressure`, bpData);
      toast.success('Blood pressure reading added successfully!');
      setShowBpDialog(false);
      setActiveBpTab('blood_pressure'); // Switch to BP tab to show latest reading
      setBpFormData({
        systolic: '',
        diastolic: '',
        pulse: '',
        method: 'Manual',
        date_recorded: '',
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await fetchAllData();
    } catch (error) {
      console.error('Error adding blood pressure reading:', error);
      toast.error('Failed to add blood pressure reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditBloodPressure = async (e) => {
    e.preventDefault();
    
    if (!bpFormData.systolic || !bpFormData.diastolic || !bpFormData.date_recorded || !bpFormData.time_recorded) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const bpData = {
        baby_id: baby.id,
        systolic: parseInt(bpFormData.systolic),
        diastolic: parseInt(bpFormData.diastolic),
        pulse: bpFormData.pulse ? parseInt(bpFormData.pulse) : null,
        method: bpFormData.method,
        date_recorded: bpFormData.date_recorded,
        time_recorded: bpFormData.time_recorded,
        notes: bpFormData.notes || null
      };
      
      await axios.put(`${API}/blood-pressure/${editingBloodPressure.id}`, bpData);
      toast.success('Blood pressure reading updated successfully!');
      setShowEditBpDialog(false);
      setEditingBloodPressure(null);
      setActiveBpTab('blood_pressure'); // Switch to BP tab to show updated reading
      setBpFormData({
        systolic: '',
        diastolic: '',
        pulse: '',
        method: 'Manual',
        date_recorded: '',
        time_recorded: new Date().toTimeString().slice(0, 5),
        notes: ''
      });
      await fetchAllData();
    } catch (error) {
      console.error('Error updating blood pressure reading:', error);
      toast.error('Failed to update blood pressure reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBloodPressure = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this blood pressure reading?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/blood-pressure/${readingId}`);
      console.log('Delete response:', response.data);
      
      toast.success('Blood pressure reading deleted successfully!');
      await fetchAllData();
      console.log('Data refreshed after deletion');
      // Stay on history tab after deletion
    } catch (error) {
      console.error('Error deleting blood pressure reading:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to delete blood pressure reading');
    }
  };

  const startEditBloodPressure = (reading) => {
    setEditingBloodPressure(reading);
    setBpFormData({
      systolic: reading.systolic.toString(),
      diastolic: reading.diastolic.toString(),
      pulse: reading.pulse ? reading.pulse.toString() : '',
      method: reading.method,
      date_recorded: reading.date_recorded,
      time_recorded: reading.time_recorded,
      notes: reading.notes || ''
    });
    setShowEditBpDialog(true);
  };

  const handleEditCondition = (condition) => {
    setEditingCondition(condition);
    setFormData({
      condition_name: condition.condition_name,
      description: condition.description || '',
      diagnosed_date: condition.diagnosed_date || '',
      status: condition.status || '',
      medication: condition.medication || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateCondition = async (e) => {
    e.preventDefault();
    if (!formData.condition_name.trim()) {
      toast.error('Please enter a condition name');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/medical-conditions/${editingCondition.id}`, {
        baby_id: baby.id,
        condition_name: formData.condition_name,
        description: formData.description,
        diagnosed_date: formData.diagnosed_date,
        status: formData.status,
        medication: formData.medication
      });
      toast.success('Medical condition updated successfully');
      setShowEditDialog(false);
      setEditingCondition(null);
      setFormData({
        condition_name: '',
        description: '',
        diagnosed_date: '',
        status: '',
        medication: ''
      });
      fetchAllData();
    } catch (error) {
      console.error('Error updating condition:', error);
      toast.error('Failed to update medical condition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCondition = async (conditionId) => {
    if (!window.confirm('Are you sure you want to delete this medical condition?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/medical-conditions/${conditionId}`);
      toast.success('Medical condition deleted');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting condition:', error);
      toast.error('Failed to delete medical condition');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Not specified';
    }
    // If already in DD/MM/YYYY format, return as is
    if (dateString && dateString.includes('/')) {
      return dateString;
    }
    // Otherwise convert from ISO format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Not specified';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-badge active',
      resolved: 'status-badge resolved',
      chronic: 'status-badge chronic'
    };
    
    return <Badge variant="outline" className={statusClasses[status] || 'status-badge active'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading medical conditions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Medical Conditions</h1>
                <p className="text-blue-200 text-sm">Manage your medical conditions and blood pressure.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Other Medical Conditions */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
              Medical Conditions
            </h3>
            {!viewOnlyMode && (
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="add-condition-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Condition
            </Button>
            )}
          </div>
          
          <div className="p-6">
            {conditions.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-conditions">
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p>No medical conditions recorded</p>
                <p className="text-sm">Add conditions to keep track of {baby.name}'s health</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conditions.map((condition) => (
                  <div key={condition.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg" data-testid={`condition-${condition.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                          {condition.condition_name}
                        </h4>
                        {getStatusBadge(condition.status)}
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">{condition.description}</p>
                      {condition.medication && (
                        <div className="text-sm text-gray-700 mt-2 bg-blue-50 p-2 rounded">
                          <span className="font-medium">Medication:</span> {condition.medication}
                        </div>
                      )}
                      <div className="text-sm text-gray-500 flex items-center space-x-1 mt-2">
                        <Calendar className="w-4 h-4" />
                        <span>Diagnosed: {formatDate(condition.diagnosed_date)}</span>
                      </div>
                      {condition.updated_by_doctor && (
                        <p className="text-xs text-purple-600 mt-2">
                          Updated by {condition.updated_by_doctor}
                          {condition.updated_by_hospital && ` - ${condition.updated_by_hospital}`}
                        </p>
                      )}
                    </div>
                    {!viewOnlyMode && (
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditCondition(condition)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteCondition(condition.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Body Temperature Monitoring */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Thermometer className="w-6 h-6 text-orange-600" />
                <h3 className="text-lg font-semibold text-orange-900" style={{fontFamily: 'Space Grotesk'}}>
                  Body Temperature
                </h3>
              </div>
              {!viewOnlyMode && (
              <Button 
                size="sm"
                onClick={() => setShowTempDialog(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Reading
              </Button>
              )}
            </div>
          </div>
          
          {/* Temperature Tabs */}
          <div className="border-b">
            <div className="flex">
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'temperature' 
                    ? 'border-orange-500 text-orange-600 bg-orange-50' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('temperature')}
              >
                Temperature
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === 'history' 
                    ? 'border-orange-500 text-orange-600 bg-orange-50' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('history')}
              >
                History
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {activeTab === 'temperature' && (
              <div>
                {temperatureReadings.length > 0 ? (
                  <div>
                    {/* Show only the latest reading */}
                    <div className="border-2 border-orange-200 bg-orange-50 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="text-3xl font-bold text-orange-600">
                              {temperatureReadings[0].temperature}°{temperatureReadings[0].unit}
                            </div>
                            <span className="text-xs bg-orange-500 text-white px-3 py-1 rounded-full">LATEST</span>
                            {temperatureReadings[0].category && (
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                temperatureReadings[0].color === 'green' ? 'bg-green-100 text-green-800' :
                                temperatureReadings[0].color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {temperatureReadings[0].category}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 space-y-1">
                            <div className="font-medium">Method: {temperatureReadings[0].method}</div>
                            <div>Date: {formatDate(temperatureReadings[0].date_recorded)} at {temperatureReadings[0].time_recorded}</div>
                            {temperatureReadings[0].notes && <div>Notes: {temperatureReadings[0].notes}</div>}
                          </div>
                          {temperatureReadings[0].updated_by_doctor && (
                            <p className="text-xs text-purple-600 mt-2">
                              Updated by {temperatureReadings[0].updated_by_doctor}
                              {temperatureReadings[0].updated_by_hospital && ` - ${temperatureReadings[0].updated_by_hospital}`}
                            </p>
                          )}
                        </div>
                        <Thermometer className="w-8 h-8 text-orange-400" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Thermometer className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>No temperature readings recorded</p>
                    <p className="text-sm">Add your first reading to start tracking</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Temperature History</h3>
                  <p className="text-sm text-gray-500">All recorded temperature readings</p>
                </div>
                
                {temperatureReadings.length > 0 ? (
                  <div className="space-y-3">
                    {temperatureReadings.map((reading, index) => (
                      <div key={reading.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-1">
                              <div className="font-semibold text-gray-900">
                                {reading.temperature}°{reading.unit}
                              </div>
                              {index === 0 && (
                                <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full font-medium">LATEST</span>
                              )}
                              {reading.category && (
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  reading.color === 'green' ? 'bg-green-100 text-green-800' :
                                  reading.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {reading.category}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatDate(reading.date_recorded)} at {reading.time_recorded} • {reading.method}
                            </div>
                            {reading.notes && (
                              <div className="text-xs text-gray-500 mt-1">{reading.notes}</div>
                            )}
                            {reading.updated_by_doctor && (
                              <p className="text-xs text-purple-600 mt-1">
                                Updated by {reading.updated_by_doctor}
                                {reading.updated_by_hospital && ` - ${reading.updated_by_hospital}`}
                              </p>
                            )}
                          </div>
                          {!viewOnlyMode && (
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => startEditTemperature(reading)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteTemperature(reading.id);
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Thermometer className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>No temperature readings recorded</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Blood Pressure Card - Separate Section */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
          <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Heart className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900" style={{fontFamily: 'Space Grotesk'}}>
                  Blood Pressure Monitoring
                </h3>
              </div>
              {!viewOnlyMode && (
                <Button 
                  size="sm"
                  onClick={() => {
                    setBpFormData(prev => ({
                      ...prev,
                      date_recorded: new Date().toLocaleDateString('en-GB').split('/').join('/')
                    }));
                    setShowBpDialog(true);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Reading
                </Button>
              )}
            </div>
          </div>

          <div>
            {/* Blood Pressure Tabs */}
            <div className="border-b">
              <div className="flex">
                <button 
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeBpTab === 'blood_pressure' 
                      ? 'border-red-500 text-red-600 bg-red-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveBpTab('blood_pressure')}
                >
                  Blood Pressure
                </button>
                <button 
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    activeBpTab === 'bp_history' 
                      ? 'border-red-500 text-red-600 bg-red-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveBpTab('bp_history')}
                >
                  History
                </button>
              </div>
            </div>

            {activeBpTab === 'blood_pressure' && (
              <div className="p-6">
                {bloodPressureReadings.length > 0 ? (
                  <div>
                    {/* Show only the latest reading - matching temperature card layout */}
                    <div className="border-2 border-red-200 bg-red-50 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="text-3xl font-bold text-red-600">
                              {bloodPressureReadings[0].systolic}/{bloodPressureReadings[0].diastolic} mmHg
                            </div>
                            <span className="text-xs bg-red-500 text-white px-3 py-1 rounded-full">LATEST</span>
                            {bloodPressureReadings[0].category && (
                              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                bloodPressureReadings[0].color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                bloodPressureReadings[0].color === 'green' ? 'bg-green-100 text-green-800' :
                                bloodPressureReadings[0].color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {bloodPressureReadings[0].category}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 space-y-1">
                            <div className="font-medium">Method: {bloodPressureReadings[0].method}</div>
                            {bloodPressureReadings[0].pulse && <div>Pulse: {bloodPressureReadings[0].pulse} bpm</div>}
                            <div>Date: {formatDate(bloodPressureReadings[0].date_recorded)} at {bloodPressureReadings[0].time_recorded}</div>
                            {bloodPressureReadings[0].notes && <div>Notes: {bloodPressureReadings[0].notes}</div>}
                          </div>
                          {bloodPressureReadings[0].updated_by_doctor && (
                            <p className="text-xs text-purple-600 mt-2">
                              Updated by {bloodPressureReadings[0].updated_by_doctor}
                              {bloodPressureReadings[0].updated_by_hospital && ` - ${bloodPressureReadings[0].updated_by_hospital}`}
                            </p>
                          )}
                        </div>
                        <Heart className="w-8 h-8 text-red-400" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>No blood pressure readings recorded</p>
                    <p className="text-sm">Start tracking by adding your first reading</p>
                  </div>
                )}
              </div>
            )}

            {activeBpTab === 'bp_history' && (
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Blood Pressure History</h3>
                  <p className="text-sm text-gray-500">All recorded blood pressure readings</p>
                </div>
                
                {bloodPressureReadings.length > 0 ? (
                  <div className="space-y-3">
                    {bloodPressureReadings.map((reading, index) => (
                      <div key={reading.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-1">
                              <div className="font-semibold text-gray-900">
                                {reading.systolic}/{reading.diastolic} mmHg
                              </div>
                              {reading.pulse && (
                                <div className="text-sm text-gray-600">
                                  Pulse: {reading.pulse} bpm
                                </div>
                              )}
                              {index === 0 && (
                                <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full font-medium">LATEST</span>
                              )}
                              {reading.category && (
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  reading.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                  reading.color === 'green' ? 'bg-green-100 text-green-800' :
                                  reading.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {reading.category}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatDate(reading.date_recorded)} at {reading.time_recorded} • {reading.method}
                            </div>
                            {reading.notes && (
                              <div className="text-xs text-gray-500 mt-1">{reading.notes}</div>
                            )}
                            {reading.updated_by_doctor && (
                              <p className="text-xs text-purple-600 mt-1">
                                Updated by {reading.updated_by_doctor}
                                {reading.updated_by_hospital && ` - ${reading.updated_by_hospital}`}
                              </p>
                            )}
                          </div>
                          {!viewOnlyMode && (
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => startEditBloodPressure(reading)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteBloodPressure(reading.id);
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>No blood pressure readings recorded</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Temperature Reading Dialog */}
        <Dialog open={showTempDialog} onOpenChange={setShowTempDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Add Temperature Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddTemperature} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature *</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    placeholder="98.7"
                    value={tempFormData.temperature}
                    onChange={(e) => setTempFormData(prev => ({ ...prev, temperature: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit *</Label>
                  <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, unit: value }))} value={tempFormData.unit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="C">Celsius (°C)</SelectItem>
                      <SelectItem value="F">Fahrenheit (°F)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="method">Method *</Label>
                <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, method: value }))} value={tempFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Oral">Oral</SelectItem>
                    <SelectItem value="Rectal">Rectal</SelectItem>
                    <SelectItem value="Axillary">Axillary (Armpit)</SelectItem>
                    <SelectItem value="Forehead">Forehead</SelectItem>
                    <SelectItem value="Ear">Ear (Tympanic)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="temp_date">Date *</Label>
                  <Input
                    id="temp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={tempFormData.date_recorded}
                    onChange={handleTempDateInput}
                    onKeyDown={handleTempDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temp_time">Time *</Label>
                  <Input
                    id="temp_time"
                    type="time"
                    value={tempFormData.time_recorded}
                    onChange={(e) => setTempFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="temp_notes">Notes (Optional)</Label>
                <Textarea
                  id="temp_notes"
                  placeholder="Any additional notes..."
                  value={tempFormData.notes}
                  onChange={(e) => setTempFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowTempDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Temperature Reading Dialog */}
        <Dialog open={showEditTempDialog} onOpenChange={setShowEditTempDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Temperature Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditTemperature} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_temperature">Temperature *</Label>
                  <Input
                    id="edit_temperature"
                    type="number"
                    step="0.1"
                    placeholder="98.7"
                    value={tempFormData.temperature}
                    onChange={(e) => setTempFormData(prev => ({ ...prev, temperature: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_unit">Unit *</Label>
                  <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, unit: value }))} value={tempFormData.unit}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="C">Celsius (°C)</SelectItem>
                      <SelectItem value="F">Fahrenheit (°F)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_method">Method *</Label>
                <Select onValueChange={(value) => setTempFormData(prev => ({ ...prev, method: value }))} value={tempFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Oral">Oral</SelectItem>
                    <SelectItem value="Rectal">Rectal</SelectItem>
                    <SelectItem value="Axillary">Axillary (Armpit)</SelectItem>
                    <SelectItem value="Forehead">Forehead</SelectItem>
                    <SelectItem value="Ear">Ear (Tympanic)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_temp_date">Date *</Label>
                  <Input
                    id="edit_temp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={tempFormData.date_recorded}
                    onChange={handleTempDateInput}
                    onKeyDown={handleTempDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_temp_time">Time *</Label>
                  <Input
                    id="edit_temp_time"
                    type="time"
                    value={tempFormData.time_recorded}
                    onChange={(e) => setTempFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_temp_notes">Notes (Optional)</Label>
                <Textarea
                  id="edit_temp_notes"
                  placeholder="Any additional notes..."
                  value={tempFormData.notes}
                  onChange={(e) => setTempFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditTempDialog(false);
                    setEditingTemperature(null);
                    setTempFormData({
                      temperature: '',
                      unit: 'F',
                      method: 'Oral',
                      date_recorded: new Date().toISOString().split('T')[0],
                      time_recorded: new Date().toTimeString().slice(0, 5),
                      notes: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Blood Pressure Reading Dialog */}
        <Dialog open={showBpDialog} onOpenChange={setShowBpDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Add Blood Pressure Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBloodPressure} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="systolic">Systolic *</Label>
                  <Input
                    id="systolic"
                    type="number"
                    placeholder="120"
                    value={bpFormData.systolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, systolic: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="diastolic">Diastolic *</Label>
                  <Input
                    id="diastolic"
                    type="number"
                    placeholder="80"
                    value={bpFormData.diastolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, diastolic: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pulse">Pulse (Optional)</Label>
                <Input
                  id="pulse"
                  type="number"
                  placeholder="72"
                  value={bpFormData.pulse}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, pulse: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bp_method">Method *</Label>
                <Select onValueChange={(value) => setBpFormData(prev => ({ ...prev, method: value }))} value={bpFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="Automatic">Automatic</SelectItem>
                    <SelectItem value="Digital">Digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bp_date">Date *</Label>
                  <Input
                    id="bp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={bpFormData.date_recorded}
                    onChange={handleBpDateInput}
                    onKeyDown={handleBpDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bp_time">Time *</Label>
                  <Input
                    id="bp_time"
                    type="time"
                    value={bpFormData.time_recorded}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bp_notes">Notes (Optional)</Label>
                <Textarea
                  id="bp_notes"
                  placeholder="Any additional notes..."
                  value={bpFormData.notes}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowBpDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Blood Pressure Reading Dialog */}
        <Dialog open={showEditBpDialog} onOpenChange={setShowEditBpDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Blood Pressure Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditBloodPressure} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_systolic">Systolic *</Label>
                  <Input
                    id="edit_systolic"
                    type="number"
                    placeholder="120"
                    value={bpFormData.systolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, systolic: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_diastolic">Diastolic *</Label>
                  <Input
                    id="edit_diastolic"
                    type="number"
                    placeholder="80"
                    value={bpFormData.diastolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, diastolic: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_pulse">Pulse (Optional)</Label>
                <Input
                  id="edit_pulse"
                  type="number"
                  placeholder="72"
                  value={bpFormData.pulse}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, pulse: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_bp_method">Method *</Label>
                <Select onValueChange={(value) => setBpFormData(prev => ({ ...prev, method: value }))} value={bpFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="Automatic">Automatic</SelectItem>
                    <SelectItem value="Digital">Digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_bp_date">Date *</Label>
                  <Input
                    id="edit_bp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={bpFormData.date_recorded}
                    onChange={handleBpDateInput}
                    onKeyDown={handleBpDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_bp_time">Time *</Label>
                  <Input
                    id="edit_bp_time"
                    type="time"
                    value={bpFormData.time_recorded}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_bp_notes">Notes (Optional)</Label>
                <Textarea
                  id="edit_bp_notes"
                  placeholder="Any additional notes..."
                  value={bpFormData.notes}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditBpDialog(false);
                    setEditingBloodPressure(null);
                    setBpFormData({
                      systolic: '',
                      diastolic: '',
                      pulse: '',
                      method: 'Manual',
                      date_recorded: '',
                      time_recorded: new Date().toTimeString().slice(0, 5),
                      notes: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Medical Condition Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Add Medical Condition</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCondition} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="condition_name">Condition Name *</Label>
                <Input
                  id="condition_name"
                  placeholder="Enter condition name"
                  value={formData.condition_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, condition_name: e.target.value }))}
                  data-testid="condition-name-input"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the medical condition"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="condition-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="diagnosed_date">Diagnosed Date</Label>
                <Input
                  id="diagnosed_date"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={formData.diagnosed_date}
                  onChange={handleConditionDateInput}
                  onKeyDown={handleConditionDateKeyDown}
                  maxLength={10}
                  data-testid="condition-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger data-testid="condition-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="chronic">Chronic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="medication">Medication</Label>
                <Textarea
                  id="medication"
                  placeholder="Enter medication details"
                  value={formData.medication}
                  onChange={(e) => setFormData(prev => ({ ...prev, medication: e.target.value }))}
                  rows={2}
                  data-testid="condition-medication-input"
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                  data-testid="save-condition-button"
                >
                  {submitting ? 'Saving...' : 'Save Condition'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowAddDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Medical Condition Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Medical Condition</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateCondition} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_condition_name">Condition Name *</Label>
                <Input
                  id="edit_condition_name"
                  placeholder="Enter condition name"
                  value={formData.condition_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, condition_name: e.target.value }))}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_description">Description</Label>
                <Textarea
                  id="edit_description"
                  placeholder="Describe the medical condition"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_diagnosed_date">Diagnosed Date</Label>
                <Input
                  id="edit_diagnosed_date"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={formData.diagnosed_date}
                  onChange={handleConditionDateInput}
                  onKeyDown={handleConditionDateKeyDown}
                  maxLength={10}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))} value={formData.status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="chronic">Chronic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_medication">Medication</Label>
                <Textarea
                  id="edit_medication"
                  placeholder="Enter medication details"
                  value={formData.medication}
                  onChange={(e) => setFormData(prev => ({ ...prev, medication: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Condition'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingCondition(null);
                    setFormData({
                      condition_name: '',
                      description: '',
                      diagnosed_date: '',
                      status: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Medical Condition Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Medical Condition</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateCondition} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_condition_name">Condition Name *</Label>
                <Input
                  id="edit_condition_name"
                  placeholder="Enter condition name"
                  value={formData.condition_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, condition_name: e.target.value }))}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_description">Description</Label>
                <Textarea
                  id="edit_description"
                  placeholder="Describe the medical condition"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_diagnosed_date">Diagnosed Date</Label>
                <Input
                  id="edit_diagnosed_date"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={formData.diagnosed_date}
                  onChange={handleConditionDateInput}
                  onKeyDown={handleConditionDateKeyDown}
                  maxLength={10}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))} value={formData.status}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="chronic">Chronic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_medication">Medication</Label>
                <Textarea
                  id="edit_medication"
                  placeholder="Enter medication details"
                  value={formData.medication}
                  onChange={(e) => setFormData(prev => ({ ...prev, medication: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Condition'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingCondition(null);
                    setFormData({
                      condition_name: '',
                      description: '',
                      diagnosed_date: '',
                      status: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Blood Pressure Reading Dialog */}
        <Dialog open={showBpDialog} onOpenChange={setShowBpDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Add Blood Pressure Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBloodPressure} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="systolic">Systolic *</Label>
                  <Input
                    id="systolic"
                    type="number"
                    placeholder="120"
                    value={bpFormData.systolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, systolic: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="diastolic">Diastolic *</Label>
                  <Input
                    id="diastolic"
                    type="number"
                    placeholder="80"
                    value={bpFormData.diastolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, diastolic: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pulse">Pulse (Heart Rate)</Label>
                <Input
                  id="pulse"
                  type="number"
                  placeholder="72"
                  value={bpFormData.pulse}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, pulse: e.target.value }))}
                />
                <p className="text-xs text-gray-500">Optional - Enter beats per minute</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bp_method">Method *</Label>
                <Select onValueChange={(value) => setBpFormData(prev => ({ ...prev, method: value }))} value={bpFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual Cuff</SelectItem>
                    <SelectItem value="Digital">Digital Monitor</SelectItem>
                    <SelectItem value="Wrist">Wrist Monitor</SelectItem>
                    <SelectItem value="Professional">Professional/Clinical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bp_date">Date *</Label>
                  <Input
                    id="bp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={bpFormData.date_recorded}
                    onChange={handleBpDateInput}
                    onKeyDown={handleBpDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bp_time">Time *</Label>
                  <Input
                    id="bp_time"
                    type="time"
                    value={bpFormData.time_recorded}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bp_notes">Notes</Label>
                <Textarea
                  id="bp_notes"
                  placeholder="Any additional notes (optional)"
                  value={bpFormData.notes}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowBpDialog(false);
                    setBpFormData({
                      systolic: '',
                      diastolic: '',
                      pulse: '',
                      method: 'Manual',
                      date_recorded: '',
                      time_recorded: new Date().toTimeString().slice(0, 5),
                      notes: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Blood Pressure Reading Dialog */}
        <Dialog open={showEditBpDialog} onOpenChange={setShowEditBpDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Blood Pressure Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditBloodPressure} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_systolic">Systolic *</Label>
                  <Input
                    id="edit_systolic"
                    type="number"
                    placeholder="120"
                    value={bpFormData.systolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, systolic: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_diastolic">Diastolic *</Label>
                  <Input
                    id="edit_diastolic"
                    type="number"
                    placeholder="80"
                    value={bpFormData.diastolic}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, diastolic: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_pulse">Pulse (Heart Rate)</Label>
                <Input
                  id="edit_pulse"
                  type="number"
                  placeholder="72"
                  value={bpFormData.pulse}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, pulse: e.target.value }))}
                />
                <p className="text-xs text-gray-500">Optional - Enter beats per minute</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_bp_method">Method *</Label>
                <Select onValueChange={(value) => setBpFormData(prev => ({ ...prev, method: value }))} value={bpFormData.method}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual Cuff</SelectItem>
                    <SelectItem value="Digital">Digital Monitor</SelectItem>
                    <SelectItem value="Wrist">Wrist Monitor</SelectItem>
                    <SelectItem value="Professional">Professional/Clinical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_bp_date">Date *</Label>
                  <Input
                    id="edit_bp_date"
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={bpFormData.date_recorded}
                    onChange={handleBpDateInput}
                    onKeyDown={handleBpDateKeyDown}
                    maxLength={10}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_bp_time">Time *</Label>
                  <Input
                    id="edit_bp_time"
                    type="time"
                    value={bpFormData.time_recorded}
                    onChange={(e) => setBpFormData(prev => ({ ...prev, time_recorded: e.target.value }))}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_bp_notes">Notes</Label>
                <Textarea
                  id="edit_bp_notes"
                  placeholder="Any additional notes (optional)"
                  value={bpFormData.notes}
                  onChange={(e) => setBpFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Reading'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditBpDialog(false);
                    setEditingBloodPressure(null);
                    setBpFormData({
                      systolic: '',
                      diastolic: '',
                      pulse: '',
                      method: 'Manual',
                      date_recorded: '',
                      time_recorded: new Date().toTimeString().slice(0, 5),
                      notes: ''
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MedicalConditions;